import PropTypes from 'prop-types';
import React from 'react';
import i18n from 'i18n-js';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import Box from '@mui/material/Box';
import Feature from 'components/Feature';
import Grid from '@mui/material/Grid';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { SkusActions } from 'state/modules/skus';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import { TextField, SwitchField } from 'components/FormFields';

const ContainerBox = styled(Box)({
  width: '100%',
});

const FormContainer = styled('form')({
  width: '60%',
});

const RowContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  marginTop: theme.spacing(2.25), // 18px
}));

const MtGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2.5), // 20px
}));

const SkuBehaviour = ({ formRef, errors, step }) => {
  const dispatch = useDispatch();
  const pricingConfig = useSelector((state) => state.config.settings?.core?.pricing || {});

  const initialValues = {
    automated: step?.automated || false,
    manually: step?.manually || !pricingConfig?.pricing_rules_set,
    default_unit_price: step?.default_unit_price || '',
  };

  const formSchema = Yup.object().shape({
    manually: Yup.boolean(),
    automated: Yup.boolean(),
    default_unit_price: Yup.string().when('manually', {
      is: true,
      then: Yup.string().required(i18n.t('common.required_field')),
    }),
  });

  const onSubmit = (values) => {
    let data = { ...values };
    Object.keys(data).forEach((key) => !data[key] && data[key] !== false && delete data[key]);
    dispatch(SkusActions.fetchCreateSku(data));
  };

  return (
    <ContainerBox>
      <Container className="h-100">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Formik
            initialValues={initialValues}
            validationSchema={formSchema}
            enableReinitialize={true}
            innerRef={formRef}
            onSubmit={onSubmit}
          >
            {({ handleSubmit, values, setFieldValue }) => (
              <FormContainer noValidate onSubmit={handleSubmit}>
                <Feature name="pricing" is="pricing_rules_set">
                  <Typography variant="h6">
                    <Box sx={{ fontWeight: 400, fontSize: 14, height: '100%' }}>
                      {i18n.t('skus.wizard.behaviour_title')}
                    </Box>
                  </Typography>
                  <RowContainer size={{ xs: 12 }}>
                    <Grid size={{ xs: 10 }}>
                      <Typography variant="h6">
                        <Box sx={{ fontWeight: 'bold', fontSize: 14, height: '100%' }}>
                          {i18n.t('skus.wizard.automated_sku_label')}
                        </Box>
                      </Typography>
                      <Typography variant="h6">
                        <Box sx={{ fontWeight: 400, fontSize: 13, height: '100%', opacity: 0.8 }}>
                          {i18n.t('skus.wizard.automated_sku_description')}
                        </Box>
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <Field
                        name="automated"
                        setFieldValue={setFieldValue}
                        color="primary"
                        component={SwitchField}
                      />
                    </Grid>
                  </RowContainer>
                </Feature>
                <RowContainer size={{ xs: 12 }}>
                  <Grid size={{ xs: 10 }}>
                    <Typography variant="h6">
                      <Box sx={{ fontWeight: 'bold', fontSize: 14, height: '100%' }}>
                        {i18n.t('skus.wizard.manually_sku_label')}
                      </Box>
                    </Typography>
                    <Typography variant="h6">
                      <Box sx={{ fontWeight: 400, fontSize: 13, height: '100%', opacity: 0.8 }}>
                        {i18n.t('skus.wizard.manually_sku_description')}
                      </Box>
                    </Typography>
                  </Grid>
                  <Feature name="pricing" is="pricing_rules_set">
                    <Grid size={{ xs: 2 }}>
                      <Field
                        name="manually"
                        setFieldValue={setFieldValue}
                        color="primary"
                        component={SwitchField}
                      />
                    </Grid>
                  </Feature>
                </RowContainer>
                {(errors?.auto || errors?.manual) && !values?.automated && !values?.manually && (
                  <FormHelperText error={true}>{errors?.auto || errors?.manual}</FormHelperText>
                )}
                {values?.manually && (
                  <MtGrid size={{ xs: 6 }}>
                    <Field
                      name="default_unit_price"
                      margin={'none'}
                      format="price"
                      type="number"
                      label={i18n.t('skus.wizard.default_price')}
                      component={TextField}
                    />
                  </MtGrid>
                )}
              </FormContainer>
            )}
          </Formik>
        </Grid>
      </Container>
    </ContainerBox>
  );
};

SkuBehaviour.propTypes = {
  formRef: PropTypes.any,
  errors: PropTypes.object,
  step: PropTypes.object,
};

export default SkuBehaviour;
